import { effect, inject, Injectable, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { activeCustomer } from '@features/customer/store/work-with-customer.signals';
import { CHATBOT_CONTEXT_IDS } from '../constants/chatbot-contexts';
import { GetCustomerNotesGQL } from '../queries/GetCustomerNotes.gql';
import { ChatbotContextService } from './chatbot-context';

@Injectable({
    providedIn: 'root',
})
export class ChatbotCustomerContextService {
    private readonly contextService = inject(ChatbotContextService);
    private readonly getCustomerNotesGQL = inject(GetCustomerNotesGQL);

    initialize(): void {
        effect(() => {
            const customer = activeCustomer();

            untracked(() => {
                if (!customer) {
                    this.contextService.remove(CHATBOT_CONTEXT_IDS.ACTIVE_CUSTOMER);

                    return;
                }

                this.updateContext(customer);
            });
        });
    }

    private async updateContext(customer: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber?: string;
        appointments?: number;
        messages?: number;
        sharingTypeId?: string | null
    }): Promise<void> {
        const isViewOnly = customer.sharingTypeId === 'VIEW_ONLY';
        const details: string[] = [
            `Name: ${customer.firstName} ${customer.lastName}`,
            `Email: ${customer.email}`
        ];

        if (isViewOnly) {
            details.push('Access: VIEW-ONLY — the agent cannot create, change or delete anything for this customer');
        }

        if (customer.phoneNumber) {
            details.push(`Phone: ${customer.phoneNumber}`);
        }

        if (customer.appointments) {
            details.push(`Appointments: ${customer.appointments}`);
        }

        if (customer.messages) {
            details.push(`Messages: ${customer.messages}`);
        }

        const notes = await this.fetchCustomerNotes(customer.id);

        if (notes) {
            details.push(`Notes: ${notes}`);
        }

        const message = details.join(', ');

        this.contextService.set(
            CHATBOT_CONTEXT_IDS.ACTIVE_CUSTOMER,
            'Working with Customer',
            message,
            {
                id: String(customer.id),
                name: `${customer.firstName} ${customer.lastName}`,
                viewOnly: String(isViewOnly)
            }
        );
    }

    private async fetchCustomerNotes(customerId: number): Promise<string | null> {
        try {
            const result = await firstValueFrom(
                this.getCustomerNotesGQL.fetch({ variables: { customerId }, context: { requireAuth: true } })
            );

            return result.data?.customer?.myCustomer?.notes ?? null;
        } catch {
            return null;
        }
    }
}
